import { create } from "zustand";

export const ZERO_STATS_GUARD_MS = 45_000;
export const ACTIVE_BROKER_STATS_POLL_MS = 2_000;
export const INACTIVE_BROKER_STATS_POLL_MS = 15_000;

const EMPTY_METRICS = {
  total_investment_amount: 0,
  current_value: 0,
  overall_profit_loss: 0,
  today_profit_loss: 0,
  today_profit_loss_percentage: 0,
  margin: 0,
};

export function normalizeBrokerFlag(flag) {
  return String(flag || "").trim().toLowerCase();
}

function readUserBrokersFromStorage() {
  try {
    const parsed = JSON.parse(localStorage.getItem("user_brokers") || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readActiveBrokerFlagFromStorage() {
  return normalizeBrokerFlag(localStorage.getItem("broker_type"));
}

const BROKER_STATS_RELOAD_CACHE_KEY = "pms_broker_stats_hydrate";

export function createEmptyBrokerStatsEntry(brokerFlag) {
  return {
    broker_flag: normalizeBrokerFlag(brokerFlag),
    ...EMPTY_METRICS,
    updatedAt: 0,
    isLoaded: false,
  };
}

export function shouldRejectZeroStatsUpdate(
  incomingStats,
  existingEntry,
  { applyRollingGuard = false } = {},
) {
  const currentValue = Number(incomingStats?.current_value) || 0;
  const overallPL = Number(incomingStats?.overall_profit_loss) || 0;

  // Non-zero snapshot is always accepted.
  if (currentValue !== 0 && overallPL !== 0) {
    return false;
  }

  // Rolling guard applies only to active-broker polling (applyRollingGuard).
  if (!applyRollingGuard) {
    return false;
  }

  const lastUpdated = Number(existingEntry?.updatedAt) || 0;
  // First successful load always accepts API data (even zeros).
  if (!lastUpdated || !existingEntry?.isLoaded) {
    return false;
  }

  // Reject zero-ish updates for 45s after the last accepted snapshot.
  return Date.now() - lastUpdated < ZERO_STATS_GUARD_MS;
}

/** @deprecated use shouldRejectZeroStatsUpdate */
export function shouldRejectActiveBrokerZeroUpdate(incomingStats, existingEntry) {
  return shouldRejectZeroStatsUpdate(incomingStats, existingEntry, {
    applyRollingGuard: true,
  });
}

function normalizeStatsPayload(brokerFlag, rawStats) {
  return {
    broker_flag: normalizeBrokerFlag(brokerFlag),
    total_investment_amount: Number(rawStats?.total_investment_amount) || 0,
    current_value: Number(rawStats?.current_value) || 0,
    overall_profit_loss: Number(rawStats?.overall_profit_loss) || 0,
    today_profit_loss: Number(rawStats?.today_profit_loss) || 0,
    today_profit_loss_percentage: Number(rawStats?.today_profit_loss_percentage) || 0,
    margin: Number(rawStats?.margin) || 0,
    updatedAt: Date.now(),
    isLoaded: true,
  };
}

export const useBrokerStatisticsStore = create((set, get) => ({
  statsByBroker: {},
  isInitialized: false,
  activeBrokerReady: false,

  initializeFromUserBrokers: () => {
    const brokers = readUserBrokersFromStorage();
    const activeFlag = readActiveBrokerFlagFromStorage();
    const statsByBroker = {};

    if (brokers.length > 0) {
      brokers.forEach((broker) => {
        const flag = normalizeBrokerFlag(broker?.broker_flag);
        if (flag) {
          statsByBroker[flag] = createEmptyBrokerStatsEntry(flag);
        }
      });
    } else if (activeFlag) {
      statsByBroker[activeFlag] = createEmptyBrokerStatsEntry(activeFlag);
    }

    try {
      const cached = sessionStorage.getItem(BROKER_STATS_RELOAD_CACHE_KEY);
      if (cached) {
        sessionStorage.removeItem(BROKER_STATS_RELOAD_CACHE_KEY);
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === "object") {
          Object.entries(parsed).forEach(([flag, entry]) => {
            const normalized = normalizeBrokerFlag(flag);
            if (normalized && statsByBroker[normalized] && entry?.isLoaded) {
              statsByBroker[normalized] = entry;
            }
          });
        }
      }
    } catch {
      // ignore corrupt cache
    }

    const activeStats = statsByBroker[activeFlag];

    set({
      statsByBroker,
      isInitialized: true,
      activeBrokerReady: Boolean(activeStats?.isLoaded),
    });
  },

  cacheStatsForPageReload: () => {
    try {
      sessionStorage.setItem(
        BROKER_STATS_RELOAD_CACHE_KEY,
        JSON.stringify(get().statsByBroker),
      );
    } catch {
      // ignore quota / private mode errors
    }
  },

  reset: () => {
    set({
      statsByBroker: {},
      isInitialized: false,
      activeBrokerReady: false,
    });
  },

  setBrokerStats: (brokerFlag, rawStats, options = {}) => {
    const flag = normalizeBrokerFlag(brokerFlag);
    if (!flag || !rawStats) return false;

    const { applyZeroGuard = false } = options;
    const existing = get().statsByBroker[flag];

    if (
      shouldRejectZeroStatsUpdate(rawStats, existing, {
        applyRollingGuard: applyZeroGuard,
      })
    ) {
      return false;
    }

    const entry = normalizeStatsPayload(flag, rawStats);
    const activeFlag = readActiveBrokerFlagFromStorage();

    set((state) => ({
      statsByBroker: {
        ...state.statsByBroker,
        [flag]: entry,
      },
      activeBrokerReady:
        flag === activeFlag && entry.isLoaded ? true : state.activeBrokerReady,
    }));

    return true;
  },

  getStatsForBroker: (brokerFlag) => {
    const flag = normalizeBrokerFlag(brokerFlag);
    return get().statsByBroker[flag] || null;
  },

  getInactiveBrokerFlags: () => {
    const activeFlag = readActiveBrokerFlagFromStorage();
    return Object.keys(get().statsByBroker).filter((flag) => flag !== activeFlag);
  },
}));

export function selectActiveBrokerStats(state) {
  const activeFlag = readActiveBrokerFlagFromStorage();
  return state.statsByBroker[activeFlag] || null;
}
